import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import {
  Actions,
  useAction,
  useActionsContext,
  useTargetedModel,
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { Tooltip } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { cn } from '@cotera/client/v0/lib/utils';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { useModalManager } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import type { ModalItem } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { useReactFlow } from '@xyflow/react';
import { useWorkflowModel } from './workflow.model';
import type { DraftConnectFrom } from './workflow-draft-graph';
import { WORKFLOW_FLOATING_PANEL_CLASS } from './workflow-editor-floating-chrome';
import { WorkflowToolbarAddNodeAction } from './workflow-toolbar-add-node.action';
import { WorkflowToolbarAddTriggerAction } from './workflow-toolbar-add-trigger.action';
import { WorkflowToolbarOpenHierarchyAction } from './workflow-toolbar-open-hierarchy.action';
import { WorkflowToolbarTestWebhookAction } from './workflow-toolbar-test-webhook.action';
import { WorkflowToolbarTriggerCronNowAction } from './workflow-toolbar-trigger-cron-now.action';
import { WorkflowToolbarTriggerEventNowAction } from './workflow-toolbar-trigger-event-now.action';
import { WorkflowToolbarTriggerWorkflowAction } from './workflow-toolbar-trigger-workflow.action';
import { WorkflowToolbarDeleteSelectionAction } from './workflow-toolbar-delete-selection.action';
import { WorkflowToolbarDuplicateSelectionAction } from './workflow-toolbar-duplicate-selection.action';
import { WorkflowCopySelectionAction } from './workflow-copy-selection.action';
import { WorkflowPasteNodesAction } from './workflow-paste-nodes.action';
import { WorkflowUndoAction } from './workflow-undo.action';
import { WorkflowRedoAction } from './workflow-redo.action';
import type { WorkflowClipboardOps } from './workflow-toolbar-action.helpers';
import {
  WORKFLOW_EDITOR_TOOLBAR_ACTIONS,
  WORKFLOW_TOOLBAR_ACTION_UI,
  type WorkflowEditorToolbarActionRef,
} from './workflow-toolbar-actions';
import { graphHasTrigger } from './workflow-editor-toolbar-state';
import { workflowModelFromContext } from './workflow-toolbar-action.helpers';
import { WorkflowWebhookTriggerFullNodeModel } from './workflow-node-models/workflow-webhook-trigger-full-node.model';
import { WorkflowWebhookTriggerPreviewNodeModel } from './workflow-node-models/workflow-webhook-trigger-preview-node.model';
import { WorkflowCronTriggerNodeModel } from './workflow-node-models/workflow-cron-trigger-node.model';
import { WorkflowEventTriggerNodeModel } from './workflow-node-models/workflow-event-trigger-node.model';
import type { WebhookTriggerInstallState } from './workflow-node-models/types';
import type { CronTriggerNodeSnapshot } from './workflow-node-models/workflow-cron-trigger-node.model';
import type { EventTriggerNodeSnapshot } from './workflow-node-models/workflow-event-trigger-node.model';
import { WorkflowDatasetTriggerNodeModel } from './workflow-node-models/workflow-dataset-trigger-node.model';
import type { DatasetTriggerNodeSnapshot } from './workflow-node-models/workflow-dataset-trigger-node.model';
import {
  WorkflowToolbarTestDatasetAction,
  type OpenWorkflowDatasetView,
} from './workflow-toolbar-test-dataset.action';
import { workflowDatasetViewEntry } from './workflow-artifact-views';
import { useOpenWorkflowResource } from './use-open-workflow-resource';
import { ResourceType } from '@cotera/api';

const TOOLBAR_MOTION_EASE = [0.4, 0, 0.2, 1] as const;

const TOOLBAR_LABEL_TRANSITION = {
  opacity: { duration: 0.12, ease: TOOLBAR_MOTION_EASE },
};

// Actions that start a run, so their button reads "Running…" and locks while one
// is in flight. The dataset one is deliberately absent: it opens the dataset
// rather than firing anything, and a button that disabled itself because an
// unrelated run was going would just refuse to navigate.
const WORKFLOW_TRIGGER_NOW_ACTIONS = new Set<WorkflowEditorToolbarActionRef>([
  WorkflowToolbarTriggerWorkflowAction,
  WorkflowToolbarTestWebhookAction,
  WorkflowToolbarTriggerCronNowAction,
  WorkflowToolbarTriggerEventNowAction,
]);

const WorkflowToolbarButtonWithTooltip: React.FC<{
  expanded: boolean;
  tooltipContent: string;
  disabled?: boolean;
  children: React.ReactElement;
}> = ({ expanded, tooltipContent, disabled, children }) => {
  if (expanded === true) {
    return children;
  }

  return (
    <Tooltip side="bottom" tooltipContent={tooltipContent}>
      <span
        className={cn(
          'inline-flex',
          disabled === true ? 'cursor-not-allowed' : undefined
        )}
      >
        {children}
      </span>
    </Tooltip>
  );
};

const WorkflowToolbarActionButton: React.FC<{
  expanded: boolean;
  icon: React.ReactNode;
  label: string;
  title?: string;
  disabled?: boolean;
  /** Reads as destructive on hover, and sits behind a divider from the rest. */
  destructive?: boolean;
  onClick: () => void;
}> = ({ expanded, icon, label, title, disabled, destructive, onClick }) => {
  const tooltipContent =
    disabled === true && title !== undefined ? title : label;

  return (
    <WorkflowToolbarButtonWithTooltip
      expanded={expanded}
      tooltipContent={tooltipContent}
      disabled={disabled}
    >
      <Button
        variant="outline"
        size="sm"
        className={cn(
          'h-8 shrink-0 justify-start overflow-hidden',
          expanded ? 'gap-1.5 px-3' : 'w-8 justify-center gap-0 px-0',
          destructive === true &&
            'text-destructive hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive'
        )}
        disabled={disabled}
        title={expanded ? title : undefined}
        onClick={onClick}
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4">
          {icon}
        </span>
        {expanded ? (
          <motion.span
            layout={false}
            initial={false}
            animate={{ opacity: 1 }}
            transition={TOOLBAR_LABEL_TRANSITION}
            className="overflow-hidden whitespace-nowrap"
          >
            {label}
          </motion.span>
        ) : null}
      </Button>
    </WorkflowToolbarButtonWithTooltip>
  );
};

const EMPTY_WEBHOOK_INSTALL_STATE =
  Watchable.fromValue<WebhookTriggerInstallState>({
    installStatus: 'draft',
    installError: null,
    loaded: false,
  });

const EMPTY_CRON_SNAPSHOT = Watchable.fromValue<CronTriggerNodeSnapshot>({
  id: '',
  name: '',
  cron: '',
  timezone: 'UTC',
  enabled: true,
});

const EMPTY_EVENT_SNAPSHOT = Watchable.fromValue<EventTriggerNodeSnapshot>({
  id: '',
  name: '',
  eventName: '',
  enabled: true,
});

const EMPTY_DATASET_SNAPSHOT = Watchable.fromValue<DatasetTriggerNodeSnapshot>({
  id: '',
  name: '',
  datasetId: '',
  criterion: 'new_row',
  trackFields: null,
  processExistingRows: false,
  runMode: 'automatic',
  enabled: true,
});

function useWorkflowToolbarReactiveState(): void {
  const model = useWorkflowModel();
  useWatchableValue(model.editorSelectionWatchable);
  useWatchableValue(model.graphWatchable);
  const webhookFull = useTargetedModel(WorkflowWebhookTriggerFullNodeModel);
  const webhookPreview = useTargetedModel(
    WorkflowWebhookTriggerPreviewNodeModel
  );
  const cronTrigger = useTargetedModel(WorkflowCronTriggerNodeModel);
  const eventTrigger = useTargetedModel(WorkflowEventTriggerNodeModel);
  const datasetTrigger = useTargetedModel(WorkflowDatasetTriggerNodeModel);
  useWatchableValue(
    webhookFull?.installStateWatchable ??
      webhookPreview?.installStateWatchable ??
      EMPTY_WEBHOOK_INSTALL_STATE
  );
  useWatchableValue(cronTrigger?.snapshotWatchable ?? EMPTY_CRON_SNAPSHOT);
  useWatchableValue(eventTrigger?.snapshotWatchable ?? EMPTY_EVENT_SNAPSHOT);
  useWatchableValue(
    datasetTrigger?.snapshotWatchable ?? EMPTY_DATASET_SNAPSHOT
  );
}

const WorkflowEditorToolbarButton: React.FC<{
  actionConstructor: WorkflowEditorToolbarActionRef;
  expanded: boolean;
  isTesting: boolean;
}> = ({ actionConstructor, expanded, isTesting }) => {
  const context = useActionsContext();
  const { execute } = useAction(actionConstructor);
  const registered = context.getAction(actionConstructor);

  if (registered === undefined || registered.applicable(context) === false) {
    return null;
  }

  const ui = WORKFLOW_TOOLBAR_ACTION_UI[actionConstructor.name];
  if (ui === undefined) {
    return null;
  }
  const isTriggerNowAction =
    WORKFLOW_TRIGGER_NOW_ACTIONS.has(actionConstructor);
  const isAddTrigger = actionConstructor === WorkflowToolbarAddTriggerAction;
  const workflowModel = isAddTrigger ? workflowModelFromContext(context) : null;
  const hasTrigger =
    workflowModel !== null &&
    graphHasTrigger(workflowModel.graphWatchable.snapshot());
  const label =
    isTriggerNowAction && isTesting
      ? 'Running…'
      : isAddTrigger && hasTrigger
      ? 'Edit trigger'
      : ui.label;
  const Icon = ui.Icon;

  return (
    <WorkflowToolbarActionButton
      expanded={expanded}
      icon={<Icon className="h-4 w-4" />}
      label={label}
      disabled={isTriggerNowAction && isTesting}
      {...(ui.destructive === true ? { destructive: true } : {})}
      onClick={() => {
        void execute();
      }}
    />
  );
};

const WorkflowEditorToolbarContent: React.FC<{
  isTesting: boolean;
  toolbarSlot: HTMLDivElement | null;
}> = ({ isTesting, toolbarSlot }) => {
  const model = useWorkflowModel();
  const toolbarState = useWatchableValue(model.toolbarStateWatchable);
  const expanded = toolbarState.expanded;
  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(null);

  useWorkflowToolbarReactiveState();

  useLayoutEffect(() => {
    setPortalTarget(toolbarSlot);
  }, [toolbarSlot]);

  const toolbarContent = (
    <motion.div
      layout
      transition={{ layout: { duration: 0.25, ease: TOOLBAR_MOTION_EASE } }}
      className={cn(
        WORKFLOW_FLOATING_PANEL_CLASS,
        'pointer-events-auto w-fit max-w-full'
      )}
    >
      <div
        className={cn(
          'flex w-max min-w-0 items-center',
          // `nowheel` so a two-finger swipe across the toolbar scrolls the
          // toolbar rather than zooming the canvas under it — the same reason
          // the inspector carries it.
          'nowheel max-w-full overflow-x-auto no-scrollbar',
          expanded ? 'gap-2' : 'gap-1'
        )}
      >
        {WORKFLOW_EDITOR_TOOLBAR_ACTIONS.map((actionConstructor) => (
          <WorkflowEditorToolbarButton
            key={actionConstructor.name}
            actionConstructor={actionConstructor}
            expanded={expanded}
            isTesting={isTesting}
          />
        ))}
        <WorkflowToolbarButtonWithTooltip
          expanded={expanded}
          tooltipContent="Expand toolbar"
        >
          <span className="inline-flex pr-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              title={expanded ? 'Collapse toolbar' : undefined}
              onClick={() => model.setToolbarExpanded(!expanded)}
            >
              {expanded ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeftOpen className="h-4 w-4" />
              )}
            </Button>
          </span>
        </WorkflowToolbarButtonWithTooltip>
      </div>
    </motion.div>
  );

  if (portalTarget === null) {
    return null;
  }

  return createPortal(toolbarContent, portalTarget);
};

/**
 * Every action the editor registers — the toolbar's buttons plus the clipboard
 * actions, which have no button and reach the user through their keyboard
 * shortcuts and the command palette.
 */
function createWorkflowEditorActions(deps: {
  openAddNodeModal: (connectFrom?: DraftConnectFrom) => void;
  /** Undefined when there is no artifact stack to open the hierarchy into. */
  getOpenHierarchyView: () => (() => void) | undefined;
  modalManager: { push: (item: ModalItem) => void; pop: () => void };
  onTestStarted: () => void;
  getWorkflow: () => TenantedClient['resource']['workflow'];
  openDatasetView: OpenWorkflowDatasetView;
  deleteSelection: () => Promise<void>;
  clipboard: WorkflowClipboardOps;
}): [
  WorkflowToolbarAddNodeAction,
  WorkflowToolbarAddTriggerAction,
  WorkflowToolbarTriggerWorkflowAction,
  WorkflowToolbarOpenHierarchyAction,
  WorkflowToolbarTestWebhookAction,
  WorkflowToolbarTriggerCronNowAction,
  WorkflowToolbarTriggerEventNowAction,
  WorkflowToolbarTestDatasetAction,
  WorkflowToolbarDuplicateSelectionAction,
  WorkflowToolbarDeleteSelectionAction,
  WorkflowCopySelectionAction,
  WorkflowPasteNodesAction,
  WorkflowUndoAction,
  WorkflowRedoAction
] {
  return [
    new WorkflowToolbarAddNodeAction(deps.openAddNodeModal),
    new WorkflowToolbarAddTriggerAction(deps.modalManager),
    new WorkflowToolbarTriggerWorkflowAction(
      deps.modalManager,
      deps.onTestStarted
    ),
    new WorkflowToolbarOpenHierarchyAction({
      canOpen: () => deps.getOpenHierarchyView() !== undefined,
      open: () => deps.getOpenHierarchyView()?.(),
    }),
    new WorkflowToolbarTestWebhookAction(
      { getWorkflow: deps.getWorkflow },
      deps.onTestStarted
    ),
    new WorkflowToolbarTriggerCronNowAction(
      { getWorkflow: deps.getWorkflow },
      deps.onTestStarted
    ),
    new WorkflowToolbarTriggerEventNowAction(
      { getWorkflow: deps.getWorkflow },
      deps.onTestStarted
    ),
    new WorkflowToolbarTestDatasetAction(deps.openDatasetView),
    new WorkflowToolbarDuplicateSelectionAction(deps.clipboard),
    new WorkflowToolbarDeleteSelectionAction({
      deleteSelection: deps.deleteSelection,
    }),
    new WorkflowCopySelectionAction(deps.clipboard),
    new WorkflowPasteNodesAction(deps.clipboard),
    new WorkflowUndoAction(deps.clipboard),
    new WorkflowRedoAction(deps.clipboard),
  ];
}

export const WorkflowEditorToolbar: React.FC<{
  onTestStarted: () => void;
  isTesting: boolean;
  toolbarSlot: HTMLDivElement | null;
  openAddNodeModal: (connectFrom?: DraftConnectFrom) => void;
  onOpenNestedTree: (() => void) | undefined;
}> = ({
  onTestStarted,
  isTesting,
  toolbarSlot,
  openAddNodeModal,
  onOpenNestedTree,
}) => {
  const client = useTenantedClient();
  const modalManager = useModalManager((s) => s.actions);
  // The add-node picker, the delete confirmation and the command palette all
  // push onto this stack, so it is the app's own answer to "is the user looking
  // at something other than the canvas right now" — which is what decides
  // whether the clipboard shortcuts may claim a keystroke.
  const isModalOpen = useModalManager((s) => s.open);
  const model = useWorkflowModel();
  // This toolbar renders as a child of `<Canvas>`, so it can drive the flow
  // directly rather than reaching for the graph mutations.
  const { getNodes, getEdges, deleteElements } = useReactFlow();
  const deleteSelection = useCallback(async (): Promise<void> => {
    await deleteElements({
      nodes: getNodes().filter((node) => node.selected === true),
      edges: getEdges().filter((edge) => edge.selected === true),
    });
  }, [deleteElements, getNodes, getEdges]);
  // Testing a dataset trigger means picking rows and running them, which is the
  // dataset page's own Run control — so this opens the same thing the trigger's
  // "Open dataset" does, by the same route. See {@link useOpenWorkflowResource}.
  const openWorkflowResource = useOpenWorkflowResource();
  const openDatasetView = useCallback<OpenWorkflowDatasetView>(
    ({ datasetId, datasetName }) => {
      openWorkflowResource({
        resourceType: ResourceType.Dataset,
        artifactId: datasetId,
        fallbackEntry: workflowDatasetViewEntry({
          workflowId: model.workflowId,
          datasetId,
          datasetName,
          fallbackTitle: 'Dataset',
        }),
      });
    },
    [openWorkflowResource, model.workflowId]
  );
  const depsRef = useRef({
    openAddNodeModal,
    modalManager,
    onTestStarted,
    openDatasetView,
    workflow: client.resource.workflow,
    deleteSelection,
    isModalOpen,
    onOpenNestedTree,
  });
  depsRef.current.openAddNodeModal = openAddNodeModal;
  depsRef.current.onTestStarted = onTestStarted;
  depsRef.current.openDatasetView = openDatasetView;
  depsRef.current.workflow = client.resource.workflow;
  depsRef.current.deleteSelection = deleteSelection;
  depsRef.current.isModalOpen = isModalOpen;
  depsRef.current.modalManager = modalManager;
  depsRef.current.onOpenNestedTree = onOpenNestedTree;

  // Built once and fed through the ref: re-creating them would unregister and
  // re-register the whole set on every render, including the ones a modal
  // opening or closing causes.
  const actions = useMemo(
    () =>
      createWorkflowEditorActions({
        openAddNodeModal: (connectFrom) =>
          depsRef.current.openAddNodeModal(connectFrom),
        modalManager: depsRef.current.modalManager,
        onTestStarted: () => depsRef.current.onTestStarted(),
        getWorkflow: () => depsRef.current.workflow,
        openDatasetView: (params) => depsRef.current.openDatasetView(params),
        deleteSelection: () => depsRef.current.deleteSelection(),
        getOpenHierarchyView: () => depsRef.current.onOpenNestedTree,
        clipboard: { isModalOpen: () => depsRef.current.isModalOpen },
      }),
    []
  );

  return (
    <Actions actions={actions}>
      <WorkflowEditorToolbarContent
        isTesting={isTesting}
        toolbarSlot={toolbarSlot}
      />
    </Actions>
  );
};
